import React from "react";

import { withFormik } from "formik";
import { compose } from "recompose";

import * as yup from "yup";
import { useFormattedMessage } from "hooks";

import NewLoader from "components/NewLoader";
import PasswordInput from "components/Input/passwordInput";

import { email, password } from "i18n/messages/formElements";
import { resetPassword } from "api";

const messages = {
    ...email,
    ...password,
    subTitle: "set_new_password",
    link_expired: "reset_password_link_expired",
    mismatchPassword: "mismatch_password",
    newPasswordPlaceholder: "new_password_placeholder",
    confirmPasswordPlaceholder: "confirm_password_placeholder",
    newPasswordRequired: "new_password_required",
    confirmPasswordRequired: "confirm_password_required",
    somethingWentWrong: "oops_something_went_wrong",
};

const ForgotPasswordForm = ({
    values,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    setFieldTouched,
}) => {
    const { formatMessage } = useFormattedMessage();

    return (
        <form method="post" onSubmit={handleSubmit}>
            <h4>{formatMessage(messages.subTitle)}</h4>
            <br />
            <PasswordInput
                id="newPassword"
                name="newPassword"
                className="lf-password-input"
                placeholder={formatMessage(messages.newPasswordPlaceholder)}
                onChange={(e) => {
                    handleChange(e);
                    setFieldTouched("newPassword", true);
                }}
                value={values.newPassword}
                onBlur={handleBlur}
                errorMessage={errors.newPassword ? formatMessage(messages[errors.newPassword]) : null}
                error={touched.newPassword && errors.newPassword}
            />
            <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                className="lf-password-input"
                placeholder={formatMessage(messages.confirmPasswordPlaceholder)}
                onChange={(e) => {
                    handleChange(e);
                    setFieldTouched("confirmPassword", true);
                }}
                value={values.confirmPassword}
                onBlur={handleBlur}
                errorMessage={errors.confirmPassword ? formatMessage(messages[errors.confirmPassword]) : null}
                error={touched.confirmPassword && errors.confirmPassword}
            />

            <br />

            {errors.requestError && (
                <span className="error pl-0" style={{ float: "left" }}>
                    {formatMessage(messages[errors.requestError])}
                </span>
            )}

            <button type="submit" disabled={isSubmitting} className={`${isSubmitting ? "disable-grey" : ""}`}>
                {isSubmitting && <NewLoader />}
                {formatMessage(password.resetPassword)}
            </button>
        </form>
    );
};

export default compose(
    withFormik({
        mapPropsToValues: () => ({
            newPassword: "",
            confirmPassword: "",
        }),
        validateOnBlur: true,
        validateOnChange: true,
        validationSchema: () =>
            yup.object().shape({
                newPassword: yup.string().required("newPasswordRequired"),
                confirmPassword: yup
                    .string()
                    .test("passwords-match", "mismatchPassword", function (value) {
                        return this.parent.newPassword === value;
                    })
                    .required("confirmPasswordRequired"),
            }),
        handleSubmit: async (values, { setSubmitting, setStatus, resetForm, props, setErrors, setFieldError }) => {
            try {
                const res = await resetPassword({
                    variables: {
                        password: values.confirmPassword,
                        hashToken: window.location.href.split(window.location.origin + "/forget-password/")?.[1],
                    },
                });

                if (res?.data?.errors?.length) {
                    const error = res?.data?.errors?.[0]?.message;
                    if (error === "link_expired") {
                        setErrors({ requestError: error });
                    } else if (error === "weakPassword") {
                        setFieldError("confirmPassword", "weakPassword");
                    } else if (error === "commonPassword") {
                        setFieldError("confirmPassword", "commonPassword");
                    } else {
                        setErrors({ requestError: "somethingWentWrong" });
                    }
                } else {
                    setStatus({ success: true });
                    resetForm({});
                    props.formSubmitSuccess();
                }
            } catch (error) {
                setStatus({ success: false });
                setSubmitting(false);
                setErrors({ requestError: "somethingWentWrong" });
            }
        },
        displayName: "ForgotPasswordForm",
    })
)(ForgotPasswordForm);
